import jsPDF from "jspdf";

/**
 * Export a Cahier des Charges as a professional PDF A4 document.
 */
export function exportSpecsPdf(title, markdownText) {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 20;
  const contentW = pageW - margin * 2;
  const dateStr = new Date().toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
  const timeStr = new Date().toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  let y = 0;

  const addFooter = (pageNum, totalPages) => {
    doc.setFontSize(7);
    doc.setTextColor(160, 160, 160);
    doc.text(`Document genere par CardFlow — ${dateStr} a ${timeStr}`, margin, pageH - 10);
    doc.text(`${pageNum}`, pageW - margin, pageH - 10, { align: "right" });
  };

  const checkPage = (needed) => {
    if (y + needed > pageH - 20) {
      addFooter(doc.getNumberOfPages(), 0);
      doc.addPage();
      y = margin;
    }
  };

  // ─── COVER PAGE ───
  // Header bar
  doc.setFillColor(79, 70, 229); // indigo-600
  doc.rect(0, 0, pageW, 55, "F");

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text("CAHIER DES CHARGES", margin, 22);

  doc.setFontSize(22);
  doc.setFont("helvetica", "bold");
  const titleLines = doc.splitTextToSize(title || "Projet sans titre", contentW);
  doc.text(titleLines, margin, 38);

  // Date
  doc.setTextColor(180, 180, 220);
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.text(dateStr, margin, 50);

  y = 70;

  // ─── CONTENT ───
  const lines = markdownText.split("\n");

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Skip the cover info (already on first page)
    if (line.startsWith("# Cahier des Charges")) continue;
    if (line.startsWith("## " + (title || "Projet"))) continue;
    if (line.startsWith("> Type de schema")) continue;
    if (line.startsWith("> Date")) continue;

    // H2 Section title
    if (line.startsWith("## ")) {
      checkPage(16);
      y += 4;
      doc.setFillColor(245, 245, 250);
      doc.roundedRect(margin - 2, y - 5, contentW + 4, 10, 2, 2, "F");
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(30, 30, 60);
      doc.text(line.slice(3), margin, y + 1);
      y += 12;
      continue;
    }

    // H3 Subsection
    if (line.startsWith("### ")) {
      checkPage(12);
      y += 2;
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(79, 70, 229);
      doc.text(line.slice(4), margin, y);
      y += 7;
      continue;
    }

    // H4
    if (line.startsWith("#### ")) {
      checkPage(10);
      doc.setFontSize(9);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(50, 50, 70);
      doc.text(line.replace(/^####\s*[■▸●◆⬡▪◇□▲]\s*/, "").slice(0, 80), margin + 2, y);
      y += 6;
      continue;
    }

    // Bold paragraph (e.g. **Title**)
    if (line.startsWith("**") && line.endsWith("**")) {
      checkPage(8);
      doc.setFontSize(9);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(40, 40, 60);
      doc.text(line.replace(/\*\*/g, ""), margin + 2, y);
      y += 6;
      continue;
    }

    // Horizontal rule
    if (line.startsWith("---")) {
      checkPage(6);
      y += 2;
      doc.setDrawColor(220, 220, 230);
      doc.setLineWidth(0.3);
      doc.line(margin, y, pageW - margin, y);
      y += 4;
      continue;
    }

    // Table header
    if (line.startsWith("|") && line.includes("|")) {
      const cells = line.split("|").map(c => c.trim()).filter(Boolean);
      // Skip separator rows
      if (cells.every(c => /^[-:]+$/.test(c))) continue;

      checkPage(7);
      const isHeader = i + 1 < lines.length && lines[i + 1]?.match(/^\|[-|: ]+\|$/);
      const colW = contentW / cells.length;

      if (isHeader) {
        doc.setFillColor(245, 245, 250);
        doc.rect(margin, y - 4, contentW, 7, "F");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(7);
        doc.setTextColor(80, 80, 100);
      } else {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(7.5);
        doc.setTextColor(60, 60, 80);
      }

      cells.forEach((cell, idx) => {
        const cellText = cell.replace(/\*\*/g, "").slice(0, 40);
        doc.text(cellText, margin + idx * colW + 2, y);
      });
      y += 6;
      continue;
    }

    // Blockquote
    if (line.startsWith("> ")) {
      checkPage(7);
      doc.setFontSize(8);
      doc.setFont("helvetica", "italic");
      doc.setTextColor(130, 130, 150);
      doc.text(line.slice(2), margin + 4, y);
      y += 5;
      continue;
    }

    // Italic footer
    if (line.startsWith("*") && line.endsWith("*")) {
      continue; // Skip — we use our own footer
    }

    // Bullet point
    if (line.startsWith("- ")) {
      checkPage(7);
      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(60, 60, 80);
      const text = line.slice(2).replace(/\*\*/g, "");
      const wrapped = doc.splitTextToSize(text, contentW - 6);
      doc.text("•", margin + 1, y);
      doc.text(wrapped, margin + 5, y);
      y += wrapped.length * 4.5;
      continue;
    }

    // Sub-bullet
    if (line.startsWith("  - ")) {
      checkPage(6);
      doc.setFontSize(7.5);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(80, 80, 100);
      doc.text("◦ " + line.slice(4).replace(/\*\*/g, ""), margin + 8, y);
      y += 4.5;
      continue;
    }

    // Empty line
    if (line.trim() === "") {
      y += 2;
      continue;
    }

    // Regular paragraph
    checkPage(7);
    doc.setFontSize(8.5);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(50, 50, 70);
    const cleanLine = line.replace(/\*\*/g, "").replace(/\*/g, "").replace(/`/g, "");
    const wrapped = doc.splitTextToSize(cleanLine, contentW);
    doc.text(wrapped, margin, y);
    y += wrapped.length * 4.5;
  }

  // Add footer to all pages
  const totalPages = doc.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);
    addFooter(p, totalPages);
  }

  doc.save(`${(title || "cahier-des-charges").replace(/\s+/g, "-").toLowerCase()}.pdf`);
}

/**
 * Export Specs PDF WITH diagram image as appendix.
 */
export async function exportSpecsPdfWithDiagram(title, markdownText) {
  // First capture the diagram image
  let diagramDataUrl = null;
  try {
    const { toPng } = await import("html-to-image");
    const flowEl = document.querySelector(".react-flow");
    if (flowEl) {
      diagramDataUrl = await toPng(flowEl, { backgroundColor: "#FFFFFF", quality: 1, pixelRatio: 2 });
    }
  } catch {
    // Continue without diagram image
  }

  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 20;
  const contentW = pageW - margin * 2;
  const dateStr = new Date().toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
  const timeStr = new Date().toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  let y = 0;

  const addFooter = (pageNum) => {
    doc.setFontSize(7);
    doc.setTextColor(160, 160, 160);
    doc.text(`Document genere par CardFlow — ${dateStr} a ${timeStr}`, margin, pageH - 10);
    doc.text(`${pageNum}`, pageW - margin, pageH - 10, { align: "right" });
  };

  const checkPage = (needed) => {
    if (y + needed > pageH - 20) {
      addFooter(doc.getNumberOfPages());
      doc.addPage();
      y = margin;
    }
  };

  // ─── COVER PAGE ───
  doc.setFillColor(79, 70, 229);
  doc.rect(0, 0, pageW, 55, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text("CAHIER DES CHARGES", margin, 22);
  doc.setFontSize(22);
  doc.setFont("helvetica", "bold");
  const titleLines2 = doc.splitTextToSize(title || "Projet sans titre", contentW);
  doc.text(titleLines2, margin, 38);
  doc.setTextColor(180, 180, 220);
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.text(dateStr, margin, 50);
  y = 70;

  // ─── CONTENT ───
  const lines = markdownText.split("\n");
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (line.startsWith("# Cahier des Charges")) continue;
    if (line.startsWith("## " + (title || "Projet"))) continue;
    if (line.startsWith("> Type de schema")) continue;
    if (line.startsWith("> Date")) continue;

    if (line.startsWith("## ")) {
      checkPage(16);
      y += 4;
      doc.setFillColor(245, 245, 250);
      doc.roundedRect(margin - 2, y - 5, contentW + 4, 10, 2, 2, "F");
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(30, 30, 60);
      doc.text(line.slice(3), margin, y + 1);
      y += 12;
      continue;
    }
    if (line.startsWith("### ")) {
      checkPage(12);
      y += 2;
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(79, 70, 229);
      doc.text(line.slice(4), margin, y);
      y += 7;
      continue;
    }
    if (line.startsWith("**") && line.endsWith("**")) {
      checkPage(8);
      doc.setFontSize(9);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(40, 40, 60);
      doc.text(line.replace(/\*\*/g, ""), margin + 2, y);
      y += 6;
      continue;
    }
    if (line.startsWith("---")) {
      checkPage(6);
      y += 2;
      doc.setDrawColor(220, 220, 230);
      doc.setLineWidth(0.3);
      doc.line(margin, y, pageW - margin, y);
      y += 4;
      continue;
    }
    if (line.startsWith("|") && line.includes("|")) {
      const cells = line.split("|").map(c => c.trim()).filter(Boolean);
      if (cells.every(c => /^[-:]+$/.test(c))) continue;
      checkPage(7);
      const isHeader = i + 1 < lines.length && lines[i + 1]?.match(/^\|[-|: ]+\|$/);
      const colW = contentW / cells.length;
      if (isHeader) {
        doc.setFillColor(245, 245, 250);
        doc.rect(margin, y - 4, contentW, 7, "F");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(7);
        doc.setTextColor(80, 80, 100);
      } else {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(7.5);
        doc.setTextColor(60, 60, 80);
      }
      cells.forEach((cell, idx) => {
        doc.text(cell.replace(/\*\*/g, "").slice(0, 40), margin + idx * colW + 2, y);
      });
      y += 6;
      continue;
    }
    if (line.startsWith("> ")) {
      checkPage(7);
      doc.setFontSize(8);
      doc.setFont("helvetica", "italic");
      doc.setTextColor(130, 130, 150);
      doc.text(line.slice(2), margin + 4, y);
      y += 5;
      continue;
    }
    if (line.startsWith("*") && line.endsWith("*")) continue;
    if (line.startsWith("- ")) {
      checkPage(7);
      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(60, 60, 80);
      const text = line.slice(2).replace(/\*\*/g, "");
      const wrapped = doc.splitTextToSize(text, contentW - 6);
      doc.text("•", margin + 1, y);
      doc.text(wrapped, margin + 5, y);
      y += wrapped.length * 4.5;
      continue;
    }
    if (line.startsWith("  - ")) {
      checkPage(6);
      doc.setFontSize(7.5);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(80, 80, 100);
      doc.text("◦ " + line.slice(4).replace(/\*\*/g, ""), margin + 8, y);
      y += 4.5;
      continue;
    }
    if (line.trim() === "") { y += 2; continue; }
    checkPage(7);
    doc.setFontSize(8.5);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(50, 50, 70);
    const cleanLine = line.replace(/\*\*/g, "").replace(/\*/g, "").replace(/`/g, "");
    const wrapped = doc.splitTextToSize(cleanLine, contentW);
    doc.text(wrapped, margin, y);
    y += wrapped.length * 4.5;
  }

  // ─── ANNEXE: SCHEMA COMPLET ───
  if (diagramDataUrl) {
    addFooter(doc.getNumberOfPages());
    doc.addPage("a4", "landscape");
    const lPageW = doc.internal.pageSize.getWidth();
    const lPageH = doc.internal.pageSize.getHeight();

    // Annexe header
    doc.setFillColor(79, 70, 229);
    doc.rect(0, 0, lPageW, 14, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text("ANNEXE — Schéma complet", 15, 9);
    doc.setFontSize(7);
    doc.setFont("helvetica", "normal");
    doc.text(`${title || "Projet"} — ${dateStr}`, lPageW - 15, 9, { align: "right" });

    // Diagram image — fit to page
    const imgMargin = 15;
    const imgW = lPageW - imgMargin * 2;
    const imgH = lPageH - 30;
    doc.addImage(diagramDataUrl, "PNG", imgMargin, 18, imgW, imgH, undefined, "FAST");

    // Footer
    doc.setFontSize(7);
    doc.setTextColor(160, 160, 160);
    doc.text(`Document genere par CardFlow — ${dateStr} a ${timeStr}`, 15, lPageH - 5);
  }

  // Add footer to all portrait pages
  const totalPages2 = doc.getNumberOfPages();
  for (let p = 1; p <= totalPages2; p++) {
    doc.setPage(p);
    // Skip the landscape appendix page (last page if diagram exists)
    if (diagramDataUrl && p === totalPages2) continue;
    addFooter(p);
  }

  doc.save(`${(title || "cahier-des-charges").replace(/\s+/g, "-").toLowerCase()}.pdf`);
}

/**
 * Export the diagram as a PNG with proper framing and CardFlow signature.
 */
export async function exportDiagramPng(title) {
  const { toPng } = await import("html-to-image");
  const flowEl = document.querySelector(".react-flow");
  if (!flowEl) throw new Error("Canvas not found");

  const dataUrl = await toPng(flowEl, {
    backgroundColor: "#FFFFFF",
    quality: 1,
    pixelRatio: 2,
  });

  // Create a canvas to add the signature
  const img = new Image();
  await new Promise((resolve, reject) => {
    img.onload = resolve;
    img.onerror = reject;
    img.src = dataUrl;
  });

  const padding = 60;
  const footerH = 50;
  const canvas = document.createElement("canvas");
  canvas.width = img.width + padding * 2;
  canvas.height = img.height + padding + footerH;
  const ctx = canvas.getContext("2d");

  // Background
  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // Draw diagram
  ctx.drawImage(img, padding, padding / 2);

  // Footer line
  const footerY = canvas.height - footerH;
  ctx.strokeStyle = "#E5E7EB";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(padding, footerY);
  ctx.lineTo(canvas.width - padding, footerY);
  ctx.stroke();

  // Title
  ctx.fillStyle = "#1F2937";
  ctx.font = "bold 20px system-ui, sans-serif";
  ctx.fillText(title || "Diagramme", padding, footerY + 24);

  // Date + signature
  const dateStr = new Date().toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" });
  ctx.fillStyle = "#9CA3AF";
  ctx.font = "13px system-ui, sans-serif";
  ctx.textAlign = "right";
  ctx.fillText(`Document genere par CardFlow — ${dateStr}`, canvas.width - padding, footerY + 24);
  ctx.textAlign = "left";

  // Download
  const link = document.createElement("a");
  link.download = `${(title || "diagramme").replace(/\s+/g, "-").toLowerCase()}.png`;
  link.href = canvas.toDataURL("image/png");
  link.click();
}

/**
 * Export diagram as a PDF page with proper framing.
 */
export async function exportDiagramPdf(title) {
  const { toPng } = await import("html-to-image");
  const flowEl = document.querySelector(".react-flow");
  if (!flowEl) throw new Error("Canvas not found");

  const dataUrl = await toPng(flowEl, {
    backgroundColor: "#FFFFFF",
    quality: 1,
    pixelRatio: 2,
  });

  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 15;
  const dateStr = new Date().toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" });

  // Header
  doc.setFillColor(79, 70, 229);
  doc.rect(0, 0, pageW, 14, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.text(title || "Diagramme", margin, 9);
  doc.setFontSize(7);
  doc.setFont("helvetica", "normal");
  doc.text(dateStr, pageW - margin, 9, { align: "right" });

  // Diagram image
  const imgW = pageW - margin * 2;
  const imgH = pageH - 30;
  doc.addImage(dataUrl, "PNG", margin, 18, imgW, imgH, undefined, "FAST");

  // Footer
  doc.setFontSize(7);
  doc.setTextColor(160, 160, 160);
  doc.text(`Document genere par CardFlow — ${dateStr}`, margin, pageH - 5);

  doc.save(`${(title || "diagramme").replace(/\s+/g, "-").toLowerCase()}-schema.pdf`);
}
